import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { localizedSiteLabel } from '@/i18n/catalogDisplayText';
import { fixedUi } from '@/i18n/fixedUi';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import {
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import { readNoticeSkc } from '@/temu-platform/utils/temuNoticeFocusQuery';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import {
  RRP_SKC_STATUS_LABELS,
} from '@/temu-platform/constants/temuApiCatalog';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import {
  adjustPriceTypeLabel,
  adjustSourceLabel,
  extractRrpSkcList,
  extractRrpSkcTotal,
  formatAdjustRejectRemark,
  formatAdjustSkuPriceLines,
  pickPricingColorLabel,
  pickRrpProductImage,
  pickRrpProductTitle,
  rrpSkcStatusLabel,
  rrpSkcStatusModifier,
} from '@/temu-platform/modules/discuss/rrpDisplay';
import { extractSiteNameList, formatSiteNameList } from '@/temu-platform/modules/discuss/discussDisplay';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import { rememberRrpPendingForHome } from '@/temu-platform/utils/temuHomeLocalSnapshot';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

const SKC_STATUS_TABS = [
  { value: '', label: '全部' },
  ...Object.entries(RRP_SKC_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

const RRP_BATCH_QUERY_LIMIT = 50;

function resolvePriceOrderSn(row) {
  return String(row?.priceOrderSn || row?.orderSn || '').trim();
}

function isPendingConfirm(row) {
  return Number(row?.status) === 1;
}

/**
 * 调价单确认 / 拒绝弹窗（对齐 bg.*.adjust.price.batch.review*）
 * mode: confirm | reject
 */
function AdjustReviewModal({
  open,
  mode = 'confirm',
  rows = [],
  busy,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation('workbench');
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (!open) return;
    setRejectReason('');
  }, [open, mode, rows]);

  if (!open || !rows.length) return null;

  const isReject = mode === 'reject';
  const title = isReject ? t('rrpOps.rejectTitle') : t('rrpOps.confirmTitle');
  const primaryLabel = isReject ? t('rrpOps.confirmReject') : t('rrpOps.confirmPass');

  const submit = () => {
    const reason = String(rejectReason || '').trim();
    if (isReject && !reason) {
      showToast(t('rrpOps.needRejectReason'), 'warning');
      return;
    }
    onSubmit({
      mode,
      rejectReason: isReject ? reason : '',
      rows,
    });
  };

  return (
    <div className="tpo-batch-modal" role="presentation">
      <div className="tpo-batch-modal__backdrop" aria-hidden="true" />
      <div
        className="tpo-batch-modal__panel tpo-discuss-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-adjust-review-title"
      >
        <header className="tpo-batch-modal__head">
          <h2 id="tpo-adjust-review-title">{title}</h2>
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onClose}>{t('moduleShell.close')}</button>
        </header>
        <div className="tpo-batch-modal__body">
          <p className="tpo-batch-modal__hint">
            {isReject
              ? t('rrpOps.rejectBatch', { count: rows.length })
              : t('rrpOps.confirmBatch', { count: rows.length })}
          </p>
          <div className="tpo-discuss-requote">
            {rows.slice(0, 8).map((row) => {
              const orderSn = resolvePriceOrderSn(row);
              const titleText = row.productName || pickRrpProductTitle(row) || t('rrpOps.orderFallback');
              const image = pickRrpProductImage(row);
              const priceLines = formatAdjustSkuPriceLines(row);
              return (
                <section key={orderSn || row.id} className="tpo-discuss-requote__row" aria-label={t('rrpOps.orderFallback')}>
                  <div className="tpo-discuss-requote__row-label">{t('rrpOps.orderFallback')}</div>
                  <div className="tpo-return-product tpo-discuss-requote__product-cell">
                    {image ? (
                      <TemuProductThumb
                        imageUrl={image}
                        className="tpo-return-product__img"
                        placeholderClassName="tpo-return-product__img tpo-return-product__img--empty"
                        alt={titleText}
                      />
                    ) : (
                      <div className="tpo-return-product__img tpo-return-product__img--empty" />
                    )}
                    <div className="tpo-return-product__meta">
                      <HoverCopyField value={titleText} className="tpo-return-product__title" popoverCopy />
                      <HoverCopyField label={t('rrpOps.orderNo')} value={orderSn} className="tpo-return-product__sku" />
                      <HoverCopyField
                        label="SKC"
                        value={String(row.skcId || row.skc_name || row.skcName || '')}
                        className="tpo-return-product__sku"
                      />
                      <HoverCopyField
                        label={t('rrpOps.priceType')}
                        value={adjustPriceTypeLabel(row.priceType)}
                        className="tpo-return-product__sku"
                      />
                      <div className="tpo-return-product__sku">
                        {t('rrpOps.priceChange', { text: priceLines.text || '—' })}
                      </div>
                    </div>
                  </div>
                </section>
              );
            })}
            {rows.length > 8 ? (
              <p className="tpo-muted">{t('rrpOps.moreHidden', { count: rows.length - 8 })}</p>
            ) : null}
            {isReject ? (
              <section className="tpo-discuss-requote__row" aria-label={t('rrpOps.rejectReason')}>
                <div className="tpo-discuss-requote__row-label">{t('rrpOps.rejectReason')}</div>
                <label className="tpo-discuss-field tpo-discuss-field--flush">
                  <span className="tpo-discuss-requote__optional">{t('rrpOps.rejectReasonHint')}</span>
                  <textarea
                    className="tpo-batch-modal__textarea"
                    value={rejectReason}
                    maxLength={255}
                    rows={3}
                    disabled={busy}
                    placeholder={t('rrpOps.rejectReasonPh')}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </label>
              </section>
            ) : null}
          </div>
        </div>
        <footer className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary" disabled={busy} onClick={onClose}>{t('shop.cancel')}</button>
          <button type="button" className="tpo-btn tpo-btn--primary" disabled={busy} onClick={submit}>
            {busy ? t('returnList.submitting') : primaryLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function RrpListPage() {
  const { t } = useTranslation('workbench');
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    loading: shopLoading,
    shopDimensionTabs,
    concreteScopeId,
  } = useTemuPageShopScope();
  const shopId = concreteScopeId;
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();

  const statusFilter = searchParams.get('status') || '';

  const [filterDraft, setFilterDraft] = useState({ skc: '', priceOrderSn: '', supplierCode: '' });
  const [query, setQuery] = useState({ skc: '', priceOrderSn: '', supplierCode: '', _nonce: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [batchModalField, setBatchModalField] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [reviewModal, setReviewModal] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
    setRows([]);
    setTotal(0);
    setError('');
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
    setLastSyncedAt('');
    setPersistedSyncedAt('');
  }, [shopId]);

  const noticeSkc = readNoticeSkc(searchParams);
  useEffect(() => {
    if (!noticeSkc) return;
    setFilterDraft((prev) => ({ ...prev, skc: noticeSkc }));
    setQuery((prev) => ({
      skc: noticeSkc,
      priceOrderSn: prev.priceOrderSn || '',
      supplierCode: prev.supplierCode || '',
      _nonce: (prev._nonce || 0) + 1,
    }));
    setPage(1);
  }, [noticeSkc, shopId]);

  useEffect(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [query._nonce, statusFilter]);

  useEffect(() => {
    if (!shopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopSyncStatus(shopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'RRP_SKC');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [shopId]);

  const refreshHomePending = useCallback(async () => {
    if (!shopId) return;
    try {
      const res = await temuPlatformAPI.getRecommendRetailPendingCount(shopId);
      rememberRrpPendingForHome(shopId, res || {});
    } catch {
      /* ignore */
    }
  }, [shopId]);

  const load = useCallback(async () => {
    if (!shopId) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const skcTokens = parseBatchTokens(query.skc).slice(0, RRP_BATCH_QUERY_LIMIT);
      const orderSnTokens = parseBatchTokens(query.priceOrderSn).slice(0, RRP_BATCH_QUERY_LIMIT);
      const supplierTokens = parseBatchTokens(query.supplierCode).slice(0, RRP_BATCH_QUERY_LIMIT);
      const body = {
        source: 'cached',
        page_num: page,
        page_size: pageSize,
        skcNameList: skcTokens,
        priceOrderSnList: orderSnTokens,
        supplierCodeList: supplierTokens,
      };
      if (statusFilter !== '' && statusFilter != null) {
        body.status = Number(statusFilter);
      }
      const res = await temuPlatformAPI.searchRecommendRetailPrice(shopId, body);
      const list = extractRrpSkcList(res);
      const totalCount = extractRrpSkcTotal(res, list.length);
      setRows(list);
      setTotal(totalCount);
      if (!skcTokens.length && !orderSnTokens.length && !supplierTokens.length) {
        await refreshHomePending();
      }
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || t('rrpOps.loadOrdersFailed'));
    } finally {
      setLoading(false);
    }
  }, [shopId, page, pageSize, statusFilter, query, refreshHomePending, t]);

  useEffect(() => {
    if (shopLoading) return;
    load();
  }, [load, shopLoading]);

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: 'rrpSkc',
        shopId,
        label: t('rrpOps.syncOrders'),
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      setQuery((prev) => ({ ...prev, _nonce: prev._nonce + 1 }));
      setPage(1);
      await refreshHomePending();
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      if (e?.message) showToast(e.message, 'error');
    }
  }, [shopId, startJob, onTrackDone, refreshHomePending, t]);

  const { refresh: guardedSync, refreshing: syncingLocal, cooldownSeconds: syncCooldown } = useGuardedRefresh(
    handleSync,
    { minIntervalMs: 3000 },
  );
  const syncing = syncingLocal || isBusy('rrpSkc', shopId);
  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const lastSyncedLabel = syncing
    ? t('productAction.syncing')
    : (latestSyncedAt
      ? t('rrpOps.lastSync', { time: formatSiteListSyncedAt(latestSyncedAt), count: total })
      : (total > 0 ? t('rrpOps.needPullTemu', { count: total }) : t('rrpOps.notSyncedTemu')));

  const statusTabs = useMemo(() => (
    <div className="tpo-status-tabs" role="tablist" aria-label={t('rrpOps.statusAria')}>
      {SKC_STATUS_TABS.map((tab) => (
        <button
          key={tab.value || 'all'}
          type="button"
          role="tab"
          aria-selected={String(statusFilter) === String(tab.value)}
          className={`tpo-status-tab${String(statusFilter) === String(tab.value) ? ' tpo-status-tab--active' : ''}`}
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            if (tab.value === '') next.delete('status');
            else next.set('status', tab.value);
            next.delete('audit');
            next.delete('view');
            setSearchParams(next, { replace: true });
            setPage(1);
          }}
        >
          {tab.value === '' ? t('tiktokCatalog.all') : t('rrpOps.adjust' + tab.value, { defaultValue: tab.label })}
          {String(statusFilter) === String(tab.value) && total > 0 ? (
            <span className="tpo-status-tab__count">{total}</span>
          ) : null}
        </button>
      ))}
    </div>
  ), [statusFilter, searchParams, setSearchParams, total, t]);

  const listTabs = useMemo(
    () => stackShopDimensionTabs(shopDimensionTabs, statusTabs),
    [shopDimensionTabs, statusTabs],
  );

  const rowKey = (row) => String(row.priceOrderSn || row.orderSn || row.skc_name || row.skcId || row.id || '');
  const selectableKeys = useMemo(() => rows.map(rowKey).filter(Boolean), [rows]);
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selectedKeys.has(k));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key, row) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  };

  useEffect(() => {
    rows.forEach((row) => {
      const key = rowKey(row);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys]);

  const onQuery = () => {
    setQuery({
      skc: filterDraft.skc,
      priceOrderSn: filterDraft.priceOrderSn,
      supplierCode: filterDraft.supplierCode,
      _nonce: query._nonce + 1,
    });
    setPage(1);
  };

  const onReset = () => {
    setFilterDraft({ skc: '', priceOrderSn: '', supplierCode: '' });
    setQuery({ skc: '', priceOrderSn: '', supplierCode: '', _nonce: query._nonce + 1 });
    setPage(1);
  };

  const batchModalMeta = {
    skc: { title: t('rrpOps.batchSkcTitle'), placeholder: t('rrpOps.batchSkcPh') },
    priceOrderSn: { title: t('rrpOps.batchOrderTitle'), placeholder: t('rrpOps.batchOrderPh') },
    supplierCode: { title: t('rrpOps.batchSupplierTitle'), placeholder: t('rrpOps.batchSupplierPh') },
  }[batchModalField] || { title: t('tiktokCatalog.batchQuery'), placeholder: t('rrpOps.batchLinePh') };

  const collectPendingSelected = () => {
    const pending = [];
    selectedKeys.forEach((key) => {
      const row = selectedRowsRef.current.get(key) || rows.find((r) => rowKey(r) === key);
      const sn = resolvePriceOrderSn(row);
      if (row && isPendingConfirm(row) && sn) {
        pending.push(row);
      }
    });
    return pending;
  };

  const openReview = (mode, targetRows) => {
    const list = (Array.isArray(targetRows) ? targetRows : []).filter(
      (r) => isPendingConfirm(r) && resolvePriceOrderSn(r),
    );
    if (!list.length) {
      showToast(t('rrpOps.pickPending'), 'warning');
      return;
    }
    setReviewModal({ mode, rows: list });
  };

  const submitReview = async ({ mode, rejectReason, rows: targetRows }) => {
    if (!shopId) return;
    const pending = (Array.isArray(targetRows) ? targetRows : [])
      .map((r) => resolvePriceOrderSn(r))
      .filter(Boolean);
    if (!pending.length) {
      showToast(t('rrpOps.missingOrderNo'), 'warning');
      return;
    }
    const result = mode === 'reject' ? 2 : 1;
    const rejectReasons = {};
    if (mode === 'reject') {
      const reason = String(rejectReason || '').trim();
      if (!reason) {
        showToast(t('rrpOps.needRejectReason'), 'warning');
        return;
      }
      pending.forEach((sn) => {
        rejectReasons[sn] = reason;
      });
    }
    setSaving(true);
    try {
      const res = await (temuPlatformAPI.batchReviewAdjustPrice || temuPlatformAPI.batchSaveRecommendRetailPrice)(shopId, {
        batchResult: result,
        submitOrders: pending,
        adjustList: pending.map((priceOrderSn) => ({ priceOrderSn, result })),
        ...(mode === 'reject' ? { rejectReasons } : {}),
      });
      if (res?.ok === false || (Number(res?.failCount) > 0 && !(Number(res?.successCount) > 0))) {
        showToast(res?.msg || (mode === 'reject' ? t('rrpOps.rejectFailed') : t('rrpOps.confirmFailed')), 'error');
      } else {
        showToast(res?.msg || t('rrpOps.submittedCount', { count: pending.length }), 'success');
        setReviewModal(null);
        setSelectedKeys(new Set());
        selectedRowsRef.current.clear();
        await load();
        await refreshHomePending();
      }
    } catch (e) {
      showToast(e?.message || (mode === 'reject' ? t('rrpOps.rejectFailed') : t('rrpOps.confirmFailed')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const batchConfirmAdjust = async () => {
    const pending = collectPendingSelected();
    if (!pending.length) {
      showToast(t('rrpOps.pickPending'), 'warning');
      return;
    }
    if (pending.length === 1) {
      openReview('confirm', pending);
      return;
    }
    const ok = await showTemuConfirm({
      title: t('rrpOps.batchConfirmTitle'),
      message: t('rrpOps.batchConfirmMsg', { count: pending.length }),
      tone: 'info',
      confirmText: t('rrpOps.confirmPass'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    await submitReview({ mode: 'confirm', rows: pending });
  };

  const batchRejectAdjust = () => {
    openReview('reject', collectPendingSelected());
  };

  const exportRows = (source) => {
    if (!source.length) {
      showToast(t('rrpOps.noneExport'), 'warning');
      return;
    }
    downloadCsv(
      `temu-adjust-${Date.now()}.csv`,
      ['priceOrderSn', 'status', 'priceType', 'source', 'adjustReason', 'productName', 'skcId', 'siteNameList', 'rejectReason', 'trafficLowExpose'],
      source.map((row) => ({
        priceOrderSn: row.priceOrderSn || row.orderSn || '',
        status: rrpSkcStatusLabel(row.status),
        priceType: adjustPriceTypeLabel(row.priceType),
        source: adjustSourceLabel(row.source),
        adjustReason: fixedUi(row.adjustReason || ''),
        productName: fixedUi(row.productName || row.product_title || row.productTitle || ''),
        skcId: row.skcId || row.skc_name || row.skcName || '',
        siteNameList: formatSiteNameList(row),
        rejectReason: formatAdjustRejectRemark(row),
        trafficLowExpose: row.trafficLowExpose ? t('returnDetail.yes') : '',
      })),
    );
    showToast(t('tiktokCatalog.exported', { count: source.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const skcTokens = parseBatchTokens(query.skc).slice(0, RRP_BATCH_QUERY_LIMIT);
      const orderSnTokens = parseBatchTokens(query.priceOrderSn).slice(0, RRP_BATCH_QUERY_LIMIT);
      const supplierTokens = parseBatchTokens(query.supplierCode).slice(0, RRP_BATCH_QUERY_LIMIT);
      const all = await fetchAllPages(async (pageNo, size) => {
        const body = {
          source: 'cached',
          page_num: pageNo,
          page_size: size,
          skcNameList: skcTokens,
          priceOrderSnList: orderSnTokens,
          supplierCodeList: supplierTokens,
        };
        if (statusFilter !== '' && statusFilter != null) {
          body.status = Number(statusFilter);
        }
        const res = await temuPlatformAPI.searchRecommendRetailPrice(shopId, body);
        const list = extractRrpSkcList(res);
        return { items: list, total: extractRrpSkcTotal(res, list.length) };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const colSpan = 12;

  return (
    <>
      <TemuSyncProgressSlot kinds={['rrpSkc']} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || saving || syncing || exporting}
            syncKinds={['rrpSkc']}
            onSync={() => {
              if (!shopId) {
                showToast(t('returnList.pickShop'), 'warning');
                return;
              }
              guardedSync();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={t('rrpOps.syncHintTemu')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(
                [...selectedKeys].map((k) => selectedRowsRef.current.get(k)).filter(Boolean),
              ),
            })}
            extraActions={[
              {
                key: 'batch-confirm-adjust',
                label: t('rrpOps.batchConfirm'),
                tone: 'primary',
                disabled: selectedKeys.size === 0 || saving,
                onClick: batchConfirmAdjust,
              },
              {
                key: 'batch-reject-adjust',
                label: t('rrpOps.batchReject'),
                disabled: selectedKeys.size === 0 || saving,
                onClick: batchRejectAdjust,
              },
            ]}
          />
        )}
      >

        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--batch tpo-field--w300">
              <span className="tpo-field__label">SKC</span>
              <input
                className="tpo-input"
                value={filterDraft.skc}
                disabled={!shopId}
                placeholder={t('rrpOps.skcPh')}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, skc: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('rrpOps.batchSkcAria')}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchModalField('skc')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="tpo-field tpo-field--inline tpo-field--batch tpo-field--w300">
              <span className="tpo-field__label">{t('rrpOps.orderNo')}</span>
              <input
                className="tpo-input"
                value={filterDraft.priceOrderSn}
                disabled={!shopId}
                placeholder={t('rrpOps.orderNoPh')}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, priceOrderSn: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('rrpOps.batchOrderAria')}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchModalField('priceOrderSn')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="tpo-field tpo-field--inline tpo-field--batch tpo-field--w300">
              <span className="tpo-field__label">{t('rrpOps.supplier')}</span>
              <input
                className="tpo-input"
                value={filterDraft.supplierCode}
                disabled={!shopId}
                placeholder={t('rrpOps.supplierPh')}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, supplierCode: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('rrpOps.batchSupplierAria')}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchModalField('supplierCode')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--secondary tpo-btn--sm"
              disabled={!shopId}
              onClick={onReset}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!shopId || loading}
              onClick={onQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
        {error ? <div className="tpo-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalMeta.title}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta.placeholder}
        onConfirm={(tokens) => {
          if (!batchModalField) return;
          const limited = tokens.slice(0, RRP_BATCH_QUERY_LIMIT);
          if (tokens.length > RRP_BATCH_QUERY_LIMIT) {
            showToast(t('rrpOps.batchLimit', { max: RRP_BATCH_QUERY_LIMIT }), 'warning');
          }
          setFilterDraft((prev) => ({ ...prev, [batchModalField]: joinBatchTokens(limited) }));
        }}
        onClose={() => setBatchModalField(null)}
      />

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('rrpOps.queryingOrders')}
      >
        <div className="tpo-drafts__table-wrap tpo-ops-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-ops-table tpo-ops-table--price tpo-ops-table--adjust">
            <colgroup>
              <col className="tpo-ops-col tpo-ops-col--check temu-product-col--check" />
              <col className="tpo-ops-col tpo-ops-col--absorb" />
              <col className="tpo-ops-col tpo-ops-col--share" />
              <col className="tpo-ops-col tpo-ops-col--share" />
              <col className="tpo-ops-col tpo-ops-col--share" />
              <col className="tpo-ops-col tpo-ops-col--share" />
              <col className="tpo-ops-col tpo-ops-col--sku-price" />
              <col className="tpo-ops-col tpo-ops-col--sku-price" />
              <col className="tpo-ops-col tpo-ops-col--sku-price" />
              <col className="tpo-ops-col tpo-ops-col--share" />
              <col className="tpo-ops-col tpo-ops-col--share" />
              <col className="tpo-ops-col tpo-ops-col--ops tpo-ops-col--share" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-ops-th tpo-ops-th--check temu-product-cell--spu-check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('rrpOps.selectAllOrders')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">{t('rrpOps.productInfo')}</th>
                <th scope="col">{t('rrpOps.orderNo')}</th>
                <th scope="col" className="tpo-ops-th--center">{t('rrpOps.status')}</th>
                <th scope="col">{t('rrpOps.priceType')}</th>
                <th scope="col">{t('rrpOps.adjustReason')}</th>
                <th scope="col" className="tpo-sku-price-group tpo-sku-price-group--start">SKU</th>
                <th scope="col" className="tpo-sku-price-group tpo-sku-price-group--mid">{t('rrpOps.oldPrice')}</th>
                <th scope="col" className="tpo-sku-price-group tpo-sku-price-group--end">{t('rrpOps.newPrice')}</th>
                <th scope="col" className="tpo-ops-th--center">{t('rrpOps.site')}</th>
                <th scope="col">{t('rrpOps.remark')}</th>
                <th scope="col" className="tpo-ops-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">
                        {!shopId
                          ? t('returnList.pickShop')
                          : loading
                            ? t('returnList.loading')
                            : t('rrpOps.emptyOrders')}
                      </p>
                      {!shopId || loading ? null : (
                        <p className="tpo-certs-empty__desc">{t('rrpOps.emptyPullTemu')}</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  const title = fixedUi(row.productName || pickRrpProductTitle(row) || '') || t('rrpOps.orderFallback');
                  const image = pickRrpProductImage(row);
                  const orderSn = resolvePriceOrderSn(row);
                  const skcId = row.skcId || row.skc_name || row.skcName || '';
                  const colorValue = pickPricingColorLabel(row);
                  const skcValue = String(skcId || '').trim();
                  const supplierCode = String(row.supplierCode || row.extCode || row.productSn || '').trim();
                  const priceLines = formatAdjustSkuPriceLines(row);
                  const sites = extractSiteNameList(row);
                  const remark = formatAdjustRejectRemark(row);
                  const canProcess = isPendingConfirm(row);
                  const statusN = Number(row.status);
                  return (
                    <tr key={key}>
                      <td
                        className="tpo-ops-td tpo-ops-td--check tpo-ops-td--vmid temu-product-cell--spu-check"
                        onClick={() => toggleOne(key, row)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('returnList.selectRow', { title: orderSn || skcId || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key, row)}
                        />
                      </td>
                      <td className="tpo-ops-td tpo-ops-td--product">
                        <div className="tpo-return-product">
                          {image ? (
                            <TemuProductThumb
                              imageUrl={image}
                              className="tpo-return-product__img"
                              placeholderClassName="tpo-return-product__img tpo-return-product__img--empty"
                              alt={title}
                            />
                          ) : (
                            <div className="tpo-return-product__img tpo-return-product__img--empty" />
                          )}
                          <div className="tpo-return-product__meta">
                            <HoverCopyField
                              value={title || ''}
                              className="tpo-return-product__title"
                              popoverCopy
                            />
                            <HoverCopyField
                              label={t('rrpOps.color')}
                              value={colorValue}
                              className="tpo-return-product__sku"
                            />
                            <HoverCopyField
                              label="SKC"
                              value={skcValue}
                              className="tpo-return-product__sku"
                            />
                            <HoverCopyField
                              label={t('rrpOps.supplier')}
                              value={supplierCode}
                              className="tpo-return-product__sku"
                            />
                          </div>
                        </div>
                      </td>
                      <td className="tpo-ops-td--vmid">
                        <HoverCopyField
                          value={orderSn}
                          display={orderSn ? <strong>{orderSn}</strong> : '—'}
                        />
                      </td>
                      <td className="tpo-ops-td--vmid tpo-ops-td--center">
                        <span className={`tpo-records__status tpo-records__status--${rrpSkcStatusModifier(row.status)}`}>
                          {t('rrpOps.adjust' + Number(row.status), { defaultValue: rrpSkcStatusLabel(row.status) })}
                        </span>
                      </td>
                      <td className="tpo-ops-td--vmid">{adjustPriceTypeLabel(row.priceType)}</td>
                      <td className="tpo-rrp-price-cell tpo-ops-td--vmid" title={row.adjustReason || ''}>
                        {fixedUi(row.adjustReason || '') || '—'}
                      </td>
                      <td className="tpo-sku-price-group tpo-sku-price-group--start">
                        <div className="tpo-rrp-price-lines">
                          {(priceLines.lines?.length ? priceLines.lines : [{ key: 'empty', head: '' }]).map((line) => (
                            <div key={`sku-${line.key || line.head}`} className="tpo-rrp-price-lines__item">
                              <div className="tpo-rrp-price-lines__sku">
                                {line.head || '—'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td
                        className="tpo-sku-price-group tpo-sku-price-group--mid"
                        title={priceLines.lines.map((l) => l.oldPrice).join('\n')}
                      >
                        <div className="tpo-rrp-price-lines">
                          {(priceLines.lines?.length ? priceLines.lines : [{ key: 'empty', oldPrice: '—' }]).map((line) => (
                            <div key={`old-${line.key || line.oldPrice}`} className="tpo-rrp-price-lines__item">
                              <div className="tpo-rrp-price-lines__amt">{line.oldPrice || '—'}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td
                        className="tpo-sku-price-group tpo-sku-price-group--end"
                        title={priceLines.lines.map((l) => l.newPrice).join('\n')}
                      >
                        <div className="tpo-rrp-price-lines">
                          {(priceLines.lines?.length ? priceLines.lines : [{ key: 'empty', newPrice: '—' }]).map((line) => (
                            <div key={`new-${line.key || line.newPrice}`} className="tpo-rrp-price-lines__item">
                              <div className="tpo-rrp-price-lines__amt">{line.newPrice || '—'}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="tpo-rrp-site-cell tpo-ops-td--vmid tpo-ops-td--center">
                        {sites.length ? (
                          <div className="tpo-site-lines">
                            {sites.map((site) => (
                              <div key={site} className="tpo-site-lines__item">{localizedSiteLabel(site)}</div>
                            ))}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="tpo-rrp-proof-cell tpo-ops-td--vmid">
                        {remark
                          || (row.trafficLowExpose ? t('rrpOps.lowTraffic') : '')
                          || (statusN === 0 ? t('rrpOps.waitPlatform') : '')
                          || (statusN === 2 ? t('rrpOps.succeeded') : '')
                          || (statusN === 3 ? t('rrpOps.failed') : '')
                          || '—'}
                      </td>
                      <td className="tpo-certs-td tpo-certs-td--ops tpo-ops-td--ops tpo-ops-td--vmid">
                        <div className="tpo-drafts__actions">
                          {canProcess ? (
                            <>
                              <button
                                type="button"
                                className="tpo-btn tpo-btn--primary tpo-btn--sm"
                                disabled={saving}
                                onClick={() => openReview('confirm', [row])}
                              >
                                {t('rrpOps.confirm')}
                              </button>
                              <button
                                type="button"
                                className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                                disabled={saving}
                                onClick={() => openReview('reject', [row])}
                              >
                                {t('rrpOps.reject')}
                              </button>
                            </>
                          ) : (
                            <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" disabled>
                              {statusN === 0 ? t('rrpOps.waitAdjust') : statusN === 2 ? t('rrpOps.succeeded') : statusN === 3 ? t('rrpOps.failed') : t('rrpOps.cannotProcess')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </ProductListCard>

      <AdjustReviewModal
        open={Boolean(reviewModal)}
        mode={reviewModal?.mode || 'confirm'}
        rows={reviewModal?.rows || []}
        busy={saving}
        onClose={() => {
          if (saving) return;
          setReviewModal(null);
        }}
        onSubmit={submitReview}
      />
    </>
  );
}
